<template>
  <div class="spm-sort-card">
    <!-- Field selector -->
    <div class="spm-sort-card__row">
      <label class="spm-sort-card__label">
        {{ t('spmTableApi.sorting.field') }}
      </label>
      <Dropdown
        v-model="selectedField"
        :options="fieldOptions"
        option-label="label"
        option-value="value"
        :placeholder="t('spmTableApi.filters.placeholders.singleSelect')"
        :show-clear="true"
        class="spm-sort-card__dropdown"
      />
    </div>

    <!-- Order selector -->
    <div
      v-if="selectedField"
      class="spm-sort-card__row"
    >
      <label class="spm-sort-card__label">
        {{ t('spmTableApi.sorting.order') }}
      </label>
      <div class="spm-sort-card__order-buttons">
        <button
          :class="['spm-sort-card__order-btn', { 'spm-sort-card__order-btn--active': selectedOrder === 'asc' }]"
          type="button"
          @click="selectedOrder = 'asc'"
        >
          <i class="fa-solid fa-arrow-up-short-wide" />
          <span>{{ t('spmTableApi.sorting.asc') }}</span>
        </button>
        <button
          :class="['spm-sort-card__order-btn', { 'spm-sort-card__order-btn--active': selectedOrder === 'desc' }]"
          type="button"
          @click="selectedOrder = 'desc'"
        >
          <i class="fa-solid fa-arrow-down-wide-short" />
          <span>{{ t('spmTableApi.sorting.desc') }}</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import {
  defineComponent,
  computed,
  ref,
  watch,
  PropType,
} from 'vue';
import { useI18n } from 'vue-i18n';
import Dropdown from 'primevue/dropdown';
import type { SpmFieldConfig, SpmActiveSort } from '../../types';

export default defineComponent({
  name: 'SpmSortInput',

  components: {
    Dropdown,
  },

  props: {
    sortableFields: {
      type: Array as PropType<SpmFieldConfig[]>,
      required: true,
    },

    modelValue: {
      type: Object as PropType<SpmActiveSort | null>,
      default: null,
    },
  },

  emits: ['update:modelValue'],

  setup(props, { emit }) {
    const { t } = useI18n();

    // Field options for dropdown (use label if available, fallback to name)
    const fieldOptions = computed(() => props.sortableFields.map((field) => ({
      value: field.name,
      label: field.label || field.name,
    })));

    // Selected field
    const selectedField = ref<string | null>(props.modelValue?.field ?? null);

    // Selected order
    const selectedOrder = ref<'asc' | 'desc'>(props.modelValue?.order ?? 'asc');

    // Watch for external changes
    watch(
      () => props.modelValue,
      (newVal) => {
        if (newVal) {
          selectedField.value = newVal.field;
          selectedOrder.value = newVal.order;
        } else {
          selectedField.value = null;
          selectedOrder.value = 'asc';
        }
      },
      { immediate: true },
    );

    // Emit changes
    const emitChange = () => {
      if (selectedField.value) {
        emit('update:modelValue', {
          field: selectedField.value,
          order: selectedOrder.value,
        });
      } else {
        emit('update:modelValue', null);
      }
    };

    watch(selectedField, emitChange);
    watch(selectedOrder, emitChange);

    return {
      t,
      fieldOptions,
      selectedField,
      selectedOrder,
    };
  },
});
</script>

<style lang="scss" scoped>
.spm-sort-card {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;

  &__row {
    display: flex;
    flex-direction: column;
    gap: 0.375rem;
  }

  &__label {
    font-size: 0.75rem;
    font-weight: 500;
    color: #666;
  }

  &__dropdown {
    width: 100%;

    :deep(.p-dropdown) {
      width: 100%;
      border-radius: 4px;
      font-size: 0.8125rem;

      &:not(.p-disabled):hover {
        border-color: #94c840;
      }

      &:not(.p-disabled).p-focus {
        border-color: #94c840;
        box-shadow: 0 0 0 1px rgba(148, 200, 64, 0.25);
      }
    }
  }

  &__order-buttons {
    display: flex;
    gap: 0.5rem;
  }

  &__order-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.375rem;
    padding: 0.5rem 0.75rem;
    border: 1px solid #ddd;
    border-radius: 4px;
    background: #fff;
    color: #666;
    font-size: 0.75rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;

    i {
      font-size: 0.75rem;
    }

    &:hover {
      border-color: #94c840;
      color: #94c840;
    }

    &--active {
      border-color: #94c840;
      background: #94c840;
      color: #fff;

      &:hover {
        background: #7ab030;
        border-color: #7ab030;
      }
    }
  }
}
</style>
